import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Check,
  Pencil,
  Mail,
  Send,
  UserPlus,
  Trash2,
  Eye,
  Save,
  Loader2,
  Ban,
  Copy,
  ExternalLink,
  Download,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const ICON_MAP = {
  approve: Check,
  reject: Ban,
  edit: Pencil,
  email: Mail,
  resend: Send,
  invite: UserPlus,
  delete: Trash2,
  view: Eye,
  save: Save,
  copy: Copy,
  link: ExternalLink,
  download: Download,
  filter: Filter,
  search: Search,
  back: ChevronLeft,
  next: ChevronRight,
};

// COMPACT (modern flat) — default for all portals except Parent
const COMPACT_VARIANT = {
  primary: 'bg-white text-[var(--color-primary)] border-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]',
  secondary: 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
  info: 'bg-white text-[var(--color-info)] border-[var(--color-info)] hover:bg-[var(--color-info-soft)]',
  warning: 'bg-white text-amber-600 border-amber-300 hover:bg-amber-50',
  danger: 'bg-white text-red-600 border-red-200 hover:bg-red-50',
  success: 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50',
  ghost: 'bg-transparent text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-50',
};

const COMPACT_ICON_COLOR = {
  primary: 'text-[var(--color-primary)]',
  secondary: 'text-slate-500',
  info: 'text-[var(--color-info)]',
  warning: 'text-amber-500',
  danger: 'text-red-500',
  success: 'text-emerald-500',
  ghost: 'text-slate-400',
};

// CLASSIC (large colored) — for Parent portal
const CLASSIC_VARIANT = {
  primary: 'ui-btn-primary',
  secondary: 'ui-btn-secondary',
  info: 'bg-[var(--color-info)] text-white hover:brightness-95 border border-transparent',
  warning: 'ui-btn-warning',
  danger: 'ui-btn-danger',
  success: 'ui-btn-success',
  ghost: 'bg-transparent text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-50',
};

const SIZE_CLASS_PATTERNS = [
  /^(?:w|h|min-w|min-h|max-w|max-h)-/,
  /^px-/, /^py-/, /^pl-/, /^pr-/, /^pt-/, /^pb-/,
  /^rounded(?:-|$)/,
  /^font-(?:thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/,
  /^leading-/,
  /^text-(?:xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl|\[(?:\d+(?:\.\d+)?(?:px|rem|em|%)?)\])$/,
];

function stripSizingClasses(className) {
  return String(className || '')
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !SIZE_CLASS_PATTERNS.some((pattern) => pattern.test(token)))
    .join(' ');
}

function ActionButton({
  variant = 'primary',
  type = 'button',
  className = '',
  disabled = false,
  loading = false,
  icon,
  onClick,
  children,
  ...buttonProps
}) {
  const location = useLocation();
  const isParentPortal = location.pathname.startsWith('/parent/');
  const isDisabled = disabled || loading;

  const IconComponent = icon ? (ICON_MAP[icon] || null) : null;

  if (isParentPortal) {
    // CLASSIC style for Parent portal (large, colored)
    const variantClass = CLASSIC_VARIANT[variant] || CLASSIC_VARIANT.primary;
    const sanitizedClassName = stripSizingClasses(className);

    return (
      <button
        type={type}
        onClick={onClick}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...buttonProps}
        className={`ui-btn-base ui-focus-ring text-center focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 ${variantClass} ${sanitizedClassName}`.trim()}
      >
        {loading && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />}
        {children}
      </button>
    );
  }

  // COMPACT style for all other portals (modern flat)
  const variantClass = COMPACT_VARIANT[variant] || COMPACT_VARIANT.primary;
  const iconColorClass = COMPACT_ICON_COLOR[variant] || COMPACT_ICON_COLOR.primary;

  const baseClass = 'inline-flex items-center justify-center gap-1.5 rounded-lg border transition-all duration-150 text-xs font-semibold';
  const sizeClass = 'h-8 px-3';
  const disabledClass = isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer active:scale-[0.97]';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...buttonProps}
      className={`${baseClass} ${sizeClass} ${variantClass} ${disabledClass} ${className}`.trim()}
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : IconComponent ? (
        <IconComponent size={14} strokeWidth={2.5} className={iconColorClass} />
      ) : null}
      {children}
    </button>
  );
}

export default ActionButton;
